import type { TicketResponse } from 'mmfintech-backend-api/types'

export const OPEN_STATUSES = ['NEW', 'OPEN', 'IN_PROGRESS']
export const MERGED_STATUS = 'MERGED'
export const CLOSED_STATUSES = ['RESOLVED', 'CLOSED', 'INVALID', MERGED_STATUS]

export const ALLOWED_ATTACHMENT_TYPES = ['.pdf', '.png', '.jpg', '.csv']
export const ALLOWED_ATTACHMENT_MIME = ['application/pdf', 'image/png', 'image/jpeg', 'text/csv']
export const MAX_ATTACHMENT_SIZE_MB = 10
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024
export const MAX_MESSAGE_LENGTH = 1000
export const MAX_INITIAL_MESSAGES = 10
// Derived, not a separate BE-enforced limit -- already guaranteed by the two caps above.
// Shown as a running total purely for UX transparency.
export const MAX_TOTAL_MESSAGE_LENGTH = MAX_MESSAGE_LENGTH * MAX_INITIAL_MESSAGES
export const TICKETS_PAGE_SIZE = 9

export function formatMessageTime(dateStr?: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${h}:${m} · ${d.getDate()} ${months[d.getMonth()]}`
}

export function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return ''
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const days = Math.floor(diffMs / 86400000)
  const hours = Math.floor(diffMs / 3600000)
  const mins = Math.floor(diffMs / 60000)
  if (days > 0) return `${days}d ago`
  if (hours > 0) return `${hours}h ago`
  if (mins > 0) return `${mins}m ago`
  return 'just now'
}

export function formatDate(dateStr?: string): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

export function isTicketOpen(status?: string): boolean {
  return OPEN_STATUSES.includes(status ?? '')
}

export function isTicketInvalid(status?: string): boolean {
  return status === 'INVALID'
}

export function isTicketMerged(status?: string): boolean {
  return status === MERGED_STATUS
}

export function getMergedIntoId(ticket?: TicketResponse): number | undefined {
  return ticket?.mergedIntoId
}

export function getMergedIds(ticket?: TicketResponse): number[] {
  return ticket?.mergedIds ?? []
}

export function getDisplayStatus(status?: string): 'open' | 'closed' {
  return isTicketOpen(status) ? 'open' : 'closed'
}

export interface FileValidationResult {
  valid: File[]
  errors: string[]
}

export function validateFiles(files: File[]): FileValidationResult {
  const valid: File[] = []
  const errors: string[] = []

  for (const file of files) {
    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`
    if (!ALLOWED_ATTACHMENT_TYPES.includes(ext)) {
      errors.push(`${file.name}: unsupported file type (allowed: ${ALLOWED_ATTACHMENT_TYPES.join(', ')})`)
    } else if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      errors.push(`${file.name}: file exceeds ${MAX_ATTACHMENT_SIZE_MB}MB limit`)
    } else {
      valid.push(file)
    }
  }

  return { valid, errors }
}
